import React from 'react';
import { observer } from 'mobx-react';
import SentenceBreakdown from 'components/sentence_breakdown';
import { DeckUI } from '@seedlang/state';
import CardTextSource from 'components/card/card_text_source';
import { pixify, depixify } from '@seedlang/utils';
import TextResizer from 'components/text_resizer';
import Text from 'components/text';
import { RevealSubtitlesIcon } from '@seedlang/icons';
import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants';
import { CardTextWrapper, BorderRadius } from '@seedlang/hoc';
import HeightLabel from 'components/height_label';
import Spinner from 'components/spinner';
import GrammarHint from 'components/grammar_hint';

const Wrapper = styled.div`
  z-index: ${Theme.z['card-3']};
  height: ${props => props.height || '100%'};
  position: ${props => props.position};
  cursor: default;
  width: 100%;
`;

const TextWrapper = styled.div`
  margin: ${props => props.margin};
  height: ${props => props.height};
  border-top-left-radius: ${props => props.borderTopLeftRadius};
  border-top-right-radius: ${props => props.borderTopRightRadius};
  border-bottom-left-radius: ${props => props.borderBottomLeftRadius};
  border-bottom-right-radius: ${props => props.borderBottomRightRadius};
  padding: ${props => props.padding};
  width: ${props => props.width || '100%'};
  left: ${props => props.left || 0};
  background: ${props => props.background || '#e8e8e8'};
  position: ${props => props.position || 'absolute'};
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Prompt = styled.div`
  margin-bottom: 10px;
`;

const GrammarHintWrapper = styled.div`
  margin-bottom: 20px;
`;

@observer
class CardText extends React.Component {

  static defaultProps = {
    languageButtonTop: '10px',
    languageButtonRight: '10px',
    position: 'relative',
    showUnderline: true,
  };

  get isWhiteBackground() {
    const background = (this.props.background || '').toString().toLowerCase();
    return background === '#fff' || background === '#ffffff' || background === 'white';
  }

  get defaultBorderColor() {
    if (this.props.defaultBorderColor) {
      return this.props.defaultBorderColor;
    }
    const background = this.props.background || '#e8e8e8';
    if (background === '#CCC' || background === '#ccc' || background === '#e8e8e8' || background === '#E6E6E6') {
      return '#b0b0b0';
    }
    return undefined;
  }

  openSourceEdit = () => {
    DeckUI.createEvent('deck - click translations icon', {
      language_id: this.props.card?.languageId || DeckUI.deck?.languageId,
      card_type_id: this.props.card?.cardType?.id || this.props.card?.cardTypeId,
    });
    this.props.onToggleSourceEditExpanded();
  }

  render() {
    return (
      <Wrapper
        position={this.props.position}
        height={pixify(this.props.height)}
      >
        {
          DeckUI.layout.debug &&
          <HeightLabel
            left={0}
          >
            {this.props.height}
          </HeightLabel>
        }
        <TextWrapper
          hasUserInput={this.props.hasUserInput}
          padding={this.props.padding}
          margin={this.props.margin}
          height={pixify(this.props.height)}
          width={this.props.width}
          position={this.props.position}
          borderTopLeftRadius={pixify(this.props.borderTopLeftRadius)}
          borderTopRightRadius={pixify(this.props.borderTopRightRadius)}
          borderBottomLeftRadius={pixify(this.props.borderBottomLeftRadius)}
          borderBottomRightRadius={pixify(this.props.borderBottomRightRadius)}
          left={this.props.left}
          background={this.props.background}
        >
          {
            this.props.grammarHint &&
              <GrammarHintWrapper>
                <GrammarHint
                  description={this.props.grammarHint.description}
                  backgroundColor={this.props.grammarHint.color}
                  tooltipId={this.props.card?.id || 'card-grammar-hint'}
                />
              </GrammarHintWrapper>
          }
          {
            !this.props.showText && this.props.showRevealTextIcon && DeckUI.userInputStores[this.props.card.id]?.inputType !== 'text' && !this.props.hideButtons &&
            <RevealSubtitlesIcon
              onClick={this.props.revealText}
              width={DeckUI.layout.minimumHeight ? 30 : 40}
              height={DeckUI.layout.minimumHeight ? 30 : 40}
              withShadow={this.isWhiteBackground}
            />
          }
          {
            this.props.cardTypeSlug === 'sentence_pronounce' && !this.props.cardState.revealed && this.props.showText && this.props.showType === 'target' &&
            <Text
              italic
              fontSize='16px'
            >
              Read the sentence out loud
            </Text>
          }
          {
            this.props.prompt && this.props.showText && this.props.showType === 'target' &&
              <Prompt>
                <Text
                italic
                fontSize='14px'
                color='#565656'
                textAlign='center'
                >
                  {this.props.prompt}
                </Text>
              </Prompt>
          }
          {
            this.props.simpleTextMode && this.props.showType === 'target' && this.props.showText &&
            <TextResizer
              resizeText
              height={depixify(this.props.height) - 20}
              width={depixify(this.props.width || DeckUI.layout.innerDeckWidth) - 40}
              characterLength={this.props.card && this.props.card.translation && this.props.card.translation.targetText && this.props.card.translation.targetText.length}
            >
              <Text
                padding='0'
                center
              >
                {this.props.sentence?.targetText}
              </Text>
            </TextResizer>
          }
          {
            !this.props.simpleTextMode && this.props.showText && !this.props.showSentenceBreakdown && this.props.showType === 'target' &&
            <TextResizer
              resizeText
              height={depixify(this.props.height) - 20}
              width={depixify(this.props.width || DeckUI.layout.innerDeckWidth) - 40}
              characterLength={this.props.customText && this.props.customText.length}
            >
              <Text
                padding='0'
                center
              >
                {this.props.customText}
              </Text>
            </TextResizer>
          }
          {
            !this.props.simpleTextMode && this.props.showText && this.props.showSentenceBreakdown && this.props.showType === 'target' && this.props.sentence && (this.props.sentence.enrichedWords || this.props.wordsAreEnriched) &&
            <TextResizer
              resizeText
              height={depixify(this.props.height) - 20}
              width={depixify(this.props.width || DeckUI.layout.innerDeckWidth) - 80}
              characterLength={this.props.sentence && this.props.sentence.targetText && this.props.sentence.targetText.length}
            >
              <SentenceBreakdown
                center
                showUnderline={this.props.showUnderline}
                blockHighlightOnClick
                blockHighlightConcept
                disableOnClick={this.props.disableOnClick}
                showCase={this.props.sentence.state.selectedWordAssociationId || this.props.showCase}
                onOpenArrowBox={this.props.onOpenArrowBox}
                hideHighlight={this.props.card.cardType.slug !== 'case-multiple-choice' && DeckUI.reviewDeck}
                highlightConceptId={DeckUI.deck.conceptId}
                highlightWords={DeckUI.deck.words}
                highlightWordAssociationIds={this.props.highlightWordAssociationIds !== undefined ? this.props.highlightWordAssociationIds : this.props.card.cardWordAssociations.map(item => item.wordAssociationId)}
                highlightType={this.props.highlightType}
                sentence={this.props.sentence}
                defaultBorderColor={this.defaultBorderColor}
              />
            </TextResizer>
          }
          {
            this.props.showText && !this.props.showHelp && this.props.showType === 'source' &&
            <CardTextSource
              resizeText={this.props.cardTypeSlug !== 'translate'}
              height={this.props.cardState.revealed ? DeckUI.layout.cardTextHeight : 200}
              width={depixify(this.props.width || DeckUI.layout.innerDeckWidth) - 40}
              translateToGerman={this.props.cardTypeSlug === 'translate' && !this.props.cardState.revealed}
              text={this.props.sourceText}
              showLiteralSource={this.props.showLiteralSource}
              literalSource={this.props.literalSource}
              user={DeckUI.user}
              onClick={this.props.showEdit ? this.openSourceEdit : null}
            />
          }
          {
            this.props.showHelp &&
            <TextResizer
              resizeText
              height={depixify(this.props.height) - 20}
              width={depixify(this.props.width || DeckUI.layout.innerDeckWidth) - 40}
              characterLength={this.props.sentence && this.props.sentence.targetText && this.props.sentence.targetText.length}
            >
              <SentenceBreakdown
                center
                disableUpdateSelectedWordAssociationId
                showUnderline={this.props.showUnderline}
                display='source'
                sentence={this.props.sentence}
                blockHighlightOnClick
                hideHighlight
                onClick={this.props.onClickHelpWord}
                hideBackground
                showCase={this.props.showCase}
                defaultBorderColor={this.defaultBorderColor}
              />
            </TextResizer>
          }
          {
            this.props.showSpinner && this.props.showSentenceBreakdown && this.props.simpleTextMode &&
              <Spinner
                width='40px'
                height='12px'
                margin='10px auto 0'
                background='#999'
              />
          }
        </TextWrapper>
        {this.props.children}
      </Wrapper>
    );
  }
}

export default BorderRadius(CardTextWrapper(CardText));
